import React, { useState } from 'react';

interface RichTextEditorProps {
  initialValue?: string;
  onChange?: (value: string) => void;
  color?: string;
  placeholder?: string;
}

interface ColoredTextProps {
  text: string;
  color?: string;
  escapeMode?: boolean;
}

const ColoredText: React.FC<ColoredTextProps> = ({ text, color = '#971eff', escapeMode = false }) => {
  // If in escape mode, just show the raw text
  if (escapeMode) {
    return <div className="whitespace-pre-wrap">{text}</div>;
  }

  // Regular expression that matches our formatting patterns while avoiding overlapping matches
  const regex = /(\*#.*?#\*|\*\*.*?\*\*|\*[^*]+\*|#{3}[^#]+#{3}|#{2}[^#]+#{2}|#{1}[^#]+#{1}|\n\* [^\n]+|\n)|([^*#\n]+)/g;
  
  // Match all parts
  const matches = Array.from(text.matchAll(regex));
  
  return (
    <div className="whitespace-pre-wrap">
      {matches.map((match, index) => {
        const part = match[0];
        
        // Handle different formatting patterns
        if (part.startsWith('*#') && part.endsWith('#*')) {
          const url = part.slice(2, -2);
          return (
            <a
              key={index}
              href={url.startsWith('http') ? url : `https://${url}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#7799E5" }}
              className="underline"
            >
              {url}
            </a>
          );
        } 
        else if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={index}>
              {part.slice(2, -2)}
            </strong>
          );
        }
        else if (part.startsWith('*') && part.endsWith('*')) {
          return (
            <span key={index} style={{ color }}>
              {part.slice(1, -1)}
            </span>
          );
        }
        else if (part.startsWith('###') && part.endsWith('###')) {
          return (
            <h1 key={index} className="text-2xl font-bold my-2">
              {part.slice(3, -3)}
            </h1>
          );
        }
        else if (part.startsWith('##') && part.endsWith('##')) {
          return (
            <h2 key={index} className="text-xl font-bold my-2">
              {part.slice(2, -2)}
            </h2>
          );
        }
        else if (part.startsWith('#') && part.endsWith('#')) {
          return (
            <h3 key={index} className="text-lg font-bold my-1">
              {part.slice(1, -1)}
            </h3>
          );
        }
        else if (part.startsWith('\n* ')) {
          return (
            <div key={index} className="flex ml-4 mt-1">
              <span className="mr-2">•</span>
              <span>{part.slice(3)}</span>
            </div>
          );
        }
        else if (part === '\n') {
          return <br key={index} />;
        }
        else {
          return <span key={index}>{part}</span>;
        }
      })}
    </div>
  );
};

// Function to escape formatting characters
const escapeFormatting = (text: string): string => {
  return text
    .replace(/\*/g, '\\*')
    .replace(/#/g, '\\#');
};

// Function to unescape formatting characters
const unescapeFormatting = (text: string): string => {
  return text
    .replace(/\\\*/g, '*')
    .replace(/\\#/g, '#');
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
  initialValue = '', 
  onChange,
  color = '#971eff',
  placeholder = "Type your formatted text here..."
}) => {
  const [value, setValue] = useState(initialValue);
  const [showPreview, setShowPreview] = useState(false);
  const [escapeMode, setEscapeMode] = useState(false);

  // Handle pasted content
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // If user wants to paste plain text and keep literal asterisks/hashtags
    if (escapeMode) {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const escapedText = escapeFormatting(pastedText);
      
      // Insert at cursor position
      const textarea = e.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      const newValue = value.substring(0, start) + escapedText + value.substring(end);
      setValue(newValue);
      if (onChange) onChange(newValue);
    } else {
      // Handle normal paste without escaping
      const pastedText = e.clipboardData.getData('text');
      
      // Insert at cursor position
      const textarea = e.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      const newValue = value.substring(0, start) + pastedText + value.substring(end);
      setValue(newValue);
      if (onChange) onChange(newValue);
      
      // Prevent default paste behavior
      e.preventDefault();
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const insertFormat = (format: string) => {
    // Get textarea element
    const textarea = document.getElementById('rich-text-area') as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    let newText = '';

    switch (format) {
      case 'color':
        newText = value.substring(0, start) + '*' + selectedText + '*' + value.substring(end);
        break;
      case 'bold':
        newText = value.substring(0, start) + '**' + selectedText + '**' + value.substring(end);
        break;
      case 'h1':
        newText = value.substring(0, start) + '###' + selectedText + '###' + value.substring(end);
        break;
      case 'h2':
        newText = value.substring(0, start) + '##' + selectedText + '##' + value.substring(end);
        break;
      case 'h3':
        newText = value.substring(0, start) + '#' + selectedText + '#' + value.substring(end);
        break;
      case 'link':
        newText = value.substring(0, start) + '*#' + (selectedText || 'url') + '#*' + value.substring(end);
        break;
      case 'bullet':
        newText = value.substring(0, start) + '\n* ' + selectedText + value.substring(end);
        break;
      case 'break':
        newText = value.substring(0, start) + '\n' + value.substring(end);
        break;
      case 'escape':
        newText = value.substring(0, start) + '\\' + selectedText + value.substring(end);
        break;
      default:
        return;
    }

    setValue(newText);
    if (onChange) {
      onChange(newText);
    }

    // Set focus back to textarea
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = format === 'link' && !selectedText ? start + 3 : start + newText.length - value.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Export function to convert the formatted text to plain text
  const toPlainText = () => {
    // Remove formatting symbols
    return value
      .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
      .replace(/\*(.*?)\*/g, '$1')     // Color
      .replace(/#{3}(.*?)#{3}/g, '$1') // H1
      .replace(/#{2}(.*?)#{2}/g, '$1') // H2
      .replace(/#{1}(.*?)#{1}/g, '$1') // H3
      .replace(/\*#(.*?)#\*/g, '$1')   // Links
      .replace(/\n\* /g, '\n• ')       // Bullet points
      .replace(/\\([*#])/g, '$1');     // Unescape escaped characters
  };

  // Clean the text for export (removing escape characters)
  const cleanForDisplay = () => {
    return unescapeFormatting(value);
  };

  return (
    <div className="border rounded-lg shadow-sm p-2 w-full">
      <div className="flex flex-wrap gap-2 mb-2 p-2 border-b">
        <button onClick={() => insertFormat('h1')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">
          H1
        </button>
        <button onClick={() => insertFormat('h2')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">
          H2
        </button>
        <button onClick={() => insertFormat('h3')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">
          H3
        </button>
        <button onClick={() => insertFormat('bold')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 font-bold">
          B
        </button>
        <button onClick={() => insertFormat('color')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">
          <span style={{ color }}>Color</span>
        </button>
        <button onClick={() => insertFormat('link')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-blue-500 underline">
          Link
        </button>
        <button onClick={() => insertFormat('bullet')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">
          • List
        </button>
        <button onClick={() => insertFormat('break')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">
          ↵ Break
        </button>
        <button 
          onClick={() => setEscapeMode(!escapeMode)} 
          className={`px-2 py-1 rounded ${escapeMode ? 'bg-yellow-200 hover:bg-yellow-300' : 'bg-gray-100 hover:bg-gray-200'}`}
          title="Toggle escape mode to show formatting symbols literally"
        >
          Escape Mode {escapeMode ? 'ON' : 'OFF'}
        </button>
        <div className="ml-auto flex gap-2">
          <button 
            onClick={() => {
              const plainText = toPlainText();
              navigator.clipboard.writeText(plainText).then(() => {
                alert('Plain text copied to clipboard!');
              });
            }} 
            className="px-2 py-1 bg-green-100 rounded hover:bg-green-200"
            title="Copy text without formatting markers"
          >
            Copy Plain
          </button>
          <button 
            onClick={() => setShowPreview(!showPreview)} 
            className="px-2 py-1 bg-blue-100 rounded hover:bg-blue-200"
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        </div>
      </div>

      {showPreview ? (
        <div className="min-h-32 p-2 border rounded">
          <ColoredText 
            text={cleanForDisplay()} 
            color={color}
            escapeMode={escapeMode} 
          />
        </div>
      ) : (
        <textarea
          id="rich-text-area"
          value={value}
          onChange={handleChange}
          onPaste={handlePaste}
          placeholder={placeholder}
          className="w-full min-h-32 p-2 outline-none resize-y border rounded"
          rows={8}
        />
      )}

      <div className="mt-2 p-2 border-t text-xs text-gray-500">
        <div>Formatting: *colored text*, **bold**, ###Heading###, ##Subheading##, #Small Heading#, *#link#*, \n* Bullet point</div>
        <div className="mt-1">To display * or # literally, use \* or \# {escapeMode && "(Escape mode is ON)"}</div>
      </div>
    </div>
  );
};

// Export RichTextEditor as the default component
export default ColoredText;
// Export the other components and types
export { RichTextEditor, type RichTextEditorProps, type ColoredTextProps };